import { Link } from 'react-router-dom';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const serviceAreas = [
  {
    county: 'Martin County',
    cities: ['Palm City', 'Stuart', 'Jensen Beach', 'Hobe Sound', 'Port St. Lucie', 'Palm Beach Gardens North', 'Indiantown', 'Rio', 'Ocean Breeze'],
  },
  {
    county: 'Palm Beach County',
    cities: ['Jupiter', 'Palm Beach Gardens', 'North Palm Beach', 'Tequesta', 'Juno Beach', 'West Palm Beach', 'Lake Worth', 'Boynton Beach', 'Delray Beach', 'Boca Raton'],
  },
  {
    county: 'St. Lucie County',
    cities: ['Fort Pierce', 'Port St. Lucie', 'Hutchinson Island', 'Stuart North', 'White City'],
  },
];

const hours = [
  { day: 'Monday – Friday', time: '8:00 AM – 6:00 PM' },
  { day: 'Saturday', time: '9:00 AM – 4:00 PM' },
  { day: 'Sunday', time: 'Closed' },
];

export default function LocationsPage() {
  const heroRef = useScrollAnimation();
  const mapRef = useScrollAnimation();
  const areasRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Page Hero */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5cc4372f7af0bd2db62aee5f90f644cf.jpg"
            alt="Service Areas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/75 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={heroRef.ref}>
          <div className={`anim-hidden ${heroRef.isVisible ? 'animate-fade-in-up' : ''} max-w-3xl`}>
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/20 text-sm" />
              <span className="text-[#C9A96E] text-sm">Locations</span>
            </div>
            <span className="luxury-divider" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">Service Areas</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Serving South<br /><span className="shimmer-gold">Florida&rsquo;s Finest</span>
            </h1>
            <p className="text-white/50 text-lg leading-relaxed max-w-xl">
              Based in Palm City, FL, we serve the entire Martin, Palm Beach, and St. Lucie County region with same-day automotive service and flexible scheduling for residential and marine projects.
            </p>
          </div>
        </div>
      </section>

      {/* Location Info + Map */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Card */}
            <div ref={mapRef.ref} className={`anim-hidden ${mapRef.isVisible ? 'animate-fade-in-up' : ''} lg:col-span-2 space-y-6`}>
              {/* Address Block */}
              <div className="bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-3xl p-8 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl">
                    <i className="ri-map-pin-line text-[#FF6B35] text-lg" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[4px] text-[#C9A96E]">Our Location</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Mr. Tint of America</h3>
                <p className="text-white/50 text-sm mb-4">1257 SW Martin Hwy<br />Palm City, FL 34990</p>
                <a
                  href="https://maps.google.com/?q=1257+SW+Martin+Hwy+Palm+City+FL+34990"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#FF6B35] hover:text-[#E55A2B] text-sm font-semibold transition-colors cursor-pointer"
                >
                  Get Directions <i className="ri-external-link-line text-xs" />
                </a>
              </div>

              {/* Hours Block */}
              <div className="bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-3xl p-8 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl">
                    <i className="ri-time-line text-[#FF6B35] text-lg" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[4px] text-[#C9A96E]">Business Hours</span>
                </div>
                <ul className="space-y-3">
                  {hours.map((h, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <span className="text-white/50 text-sm">{h.day}</span>
                      <span className={`text-sm font-medium ${h.time === 'Closed' ? 'text-red-400' : 'text-white'}`}>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Block */}
              <div className="bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-3xl p-8 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl">
                    <i className="ri-phone-line text-[#FF6B35] text-lg" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[4px] text-[#C9A96E]">Contact</span>
                </div>
                <a href="tel:+15612759473" className="block text-2xl font-bold text-white hover:text-[#FF6B35] transition-colors mb-2 cursor-pointer" style={{ fontFamily: 'Sora, sans-serif' }}>
                  (561) 275-9473
                </a>
                <p className="text-white/40 text-xs mb-6">Call or text for fastest response</p>
                <Link
                  to="/#quote"
                  className="block w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white text-center py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Request a Free Quote
                </Link>
              </div>
            </div>

            {/* Map */}
            <div className={`anim-hidden ${mapRef.isVisible ? 'animate-fade-in-up delay-200' : ''} lg:col-span-3 rounded-3xl overflow-hidden border border-white/5 h-[600px] lg:h-auto min-h-[400px]`}>
              <iframe
                title="Mr. Tint of America Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3538.1862741024855!2d-80.27217768489752!3d27.161670182971!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88de7c0e1a1a1a1a%3A0x0!2s1257+SW+Martin+Hwy%2C+Palm+City%2C+FL+34990!5e0!3m2!1sen!2sus!4v1620000000000"
                className="w-full h-full border-0 grayscale contrast-125 brightness-75"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div ref={areasRef.ref} className={`anim-hidden ${areasRef.isVisible ? 'animate-fade-in-up' : ''} text-center mb-16`}>
            <span className="luxury-divider mx-auto" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">Coverage Area</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              We Come to <span className="shimmer-gold">Your Neighborhood</span>
            </h2>
            <p className="text-white/40 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
              Based in Palm City with mobile service throughout South Florida. Residential and marine appointments available at your location.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {serviceAreas.map((area, i) => (
              <div
                key={i}
                className={`anim-hidden ${areasRef.isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} bg-[#111111] border border-white/5 hover:border-[#C9A96E]/30 rounded-3xl p-8 transition-all duration-300 cursor-default`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#FF6B35]/20 to-[#C9A96E]/10 rounded-xl">
                    <i className="ri-map-pin-2-line text-[#FF6B35] text-lg" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>{area.county}</h3>
                    <span className="text-xs text-white/30">{area.cities.length} cities served</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {area.cities.map((city) => (
                    <span
                      key={city}
                      className="px-3 py-1.5 bg-white/5 hover:bg-[#FF6B35]/10 hover:border-[#FF6B35]/30 border border-white/5 rounded-full text-xs text-white/60 hover:text-white transition-all cursor-default"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className={`anim-hidden ${areasRef.isVisible ? 'animate-fade-in-up delay-400' : ''} mt-10 bg-[#111111] border border-[#C9A96E]/15 rounded-3xl p-8 text-center`}>
            <i className="ri-map-line text-[#C9A96E] text-3xl mb-3 block" />
            <p className="text-white text-base font-semibold mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Not seeing your city?</p>
            <p className="text-white/40 text-sm mb-5">We frequently travel beyond our listed areas. Give us a call — we may be closer than you think.</p>
            <a
              href="tel:+15612759473"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B35]/50 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all hover:bg-[#FF6B35]/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-[#FF6B35]" />
              (561) 275-9473
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
